import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { SearchInput } from "@/components/common/SearchInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dateFmt } from "@/lib/format";
import { Laptop, Plus, Trash2, UserX } from "lucide-react";
import { toast } from "sonner";

const STATUS_FAMILY = { in_stock: "slate", assigned: "blue", maintenance: "amber", retired: "red" };
const EMPTY_FORM = { name: "", category: "Laptop", serial_number: "", assigned_to: "", expected_return_date: "", condition: "good", notes: "" };

export default function Assets() {
  const qc = useQueryClient();
  const { me, can } = useApp();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const { data: assets, isLoading } = useQuery({ queryKey: ["assets"], queryFn: () => get("/hrms/assets") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });

  const create = useMutation({
    mutationFn: (body) => post("/hrms/assets", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["assets"] }); toast.success("Asset added"); setAdding(false); setForm(EMPTY_FORM); },
  });
  const update = useMutation({
    mutationFn: ({ id, body }) => patch(`/hrms/assets/${id}`, { ...body, _actor: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["assets"] }); toast.success("Asset updated"); },
  });
  const remove = useMutation({
    mutationFn: (id) => del(`/hrms/assets/${id}?_actor=${me?.id || ""}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["assets"] }); toast.success("Asset removed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not delete"),
  });

  const categories = useMemo(
    () => Array.from(new Set((assets || []).map((a) => a.category).filter(Boolean))).sort(),
    [assets]
  );

  const filtered = useMemo(() => {
    return (assets || []).filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (categoryFilter !== "all" && a.category !== categoryFilter) return false;
      if (search && !`${a.name} ${a.serial_number}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [assets, statusFilter, categoryFilter, search]);

  return (
    <div>
      <PageHeader title="Asset Management" subtitle="Company equipment issued to and returned by employees.">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="asset-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_FAMILY).map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        {can("hrms", "edit") && <Button data-testid="add-asset-btn" onClick={() => setAdding((s) => !s)}><Plus className="mr-1.5 h-4 w-4" />Add Asset</Button>}
      </PageHeader>

      <div className="mb-4 flex items-center gap-3">
        <SearchInput testid="asset-search" placeholder="Search name or serial number" value={search} onChange={setSearch} />
        {categories.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-44" data-testid="asset-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {isLoading ? <Skeleton className="h-64" /> : (
        <Section>
          {adding && (
            <div className="border-b border-border/60 bg-muted/30 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="asset-name" className="mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
                <div><Label>Category</Label><Input data-testid="asset-category" className="mt-1" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} /></div>
                <div><Label>Serial Number</Label><Input data-testid="asset-serial" className="mt-1" value={form.serial_number} onChange={(e) => setForm((f) => ({ ...f, serial_number: e.target.value }))} /></div>
                <div>
                  <Label>Assign To (optional)</Label>
                  <Select value={form.assigned_to} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v }))}>
                    <SelectTrigger className="mt-1" data-testid="asset-assign"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" data-testid="asset-submit" onClick={() => create.mutate({ ...form, assigned_to: form.assigned_to || null, _actor: me?.id })}>Add</Button>
              </div>
            </div>
          )}
          {!filtered.length ? (
            <EmptyState icon={Laptop} title={(assets || []).length === 0 ? "No assets" : "No matching assets"}
              subtitle={(assets || []).length === 0 ? undefined : "Adjust your search or filters."} />
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((a) => (
                <div key={a.id} className="group flex items-center gap-3 px-4 py-3 text-sm">
                  <Laptop className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium">{a.name}</div>
                    <div className="text-xs text-muted-foreground">{a.category} · {a.serial_number}{a.expected_return_date ? ` · due back ${dateFmt(a.expected_return_date)}` : ""}</div>
                  </div>
                  <span className="text-xs text-muted-foreground">{a.assigned_to_name || "Unassigned"}</span>
                  <StatusBadge family={STATUS_FAMILY[a.status]} label={a.status.replace("_", " ")} />
                  {can("hrms", "edit") && a.status === "assigned" && (
                    <Button size="sm" variant="ghost" data-testid={`unassign-${a.id}`} onClick={() => update.mutate({ id: a.id, body: { assigned_to: null } })}>
                      <UserX className="mr-1 h-3.5 w-3.5" />Unassign
                    </Button>
                  )}
                  {can("hrms", "delete") && (
                    <ConfirmDelete testid={`del-asset-${a.id}`} title={`Delete ${a.name}?`}
                      trigger={<button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                      onConfirm={() => remove.mutate(a.id)} />
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
